// API Service Layer with common HTTP methods
import { API_URL } from '../config/api';

// Types for API responses
export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  user?: T;
  companies?: T;
  schools?: T;
  [key: string]: any;
}

// Token management
const getToken = (): string | null => {
  return localStorage.getItem('token');
};

const getAuthHeaders = (): HeadersInit => {
  const token = getToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    (headers as any)['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

const getAuthHeadersForFormData = (): HeadersInit => {
  const token = getToken();
  const headers: HeadersInit = {};
  
  if (token) {
    (headers as any)['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

// Handle API responses and errors
const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    // Handle authentication errors
    if (response.status === 401) {
      // Check if this is a login request (no token in headers)
      const isLoginRequest = !response.url.includes('refresh') && !getToken();
      
      if (isLoginRequest) {
        // For login failures, return the error response data instead of redirecting
        try {
          const errorData = await response.json();
          return errorData as T;
        } catch (parseError) {
          throw new Error('Login failed');
        }
      }
      
      // For other 401 errors (authenticated requests), try to refresh token
      const refreshResult = await tryRefreshToken();
      if (refreshResult) {
        // Token refreshed successfully, but this request already failed
        // The user should retry the action manually
        throw new Error('Token expired. Please try again.');
      } else {
        // Refresh failed, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/auth/signin';
        throw new Error('Authentication failed');
      }
    }
    
    // Initialize with HTTP status, but prioritize backend messages
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    let errorData: any = null;
    
    try {
      errorData = await response.json();
      
      // Handle validation errors (422) - preserve error structure for frontend
      if (response.status === 422 && errorData.errors) {
        // For validation errors, return the full response data instead of throwing
        // This allows the frontend to access both message and errors fields
        return errorData as T;
      }
      
      // Handle 400 Bad Request errors - preserve error structure for frontend
      if (response.status === 400) {
        // For 400 errors, return the full response data instead of throwing
        // This allows the frontend to access the backend message and error details
        return errorData as T;
      }
      
      // Handle ELIGIBILITY_VIOLATION errors with detailed information
      if (errorData.error === 'ELIGIBILITY_VIOLATION' && errorData.errors && Array.isArray(errorData.errors)) {
        // Create a comprehensive error message for eligibility violations
        const constraintErrors = errorData.errors.join('\n\n');
        let detailedMessage = `${errorData.message}\n\n${constraintErrors}`;
        
        // Add existing teams information if available
        if (errorData.existing_teams && errorData.existing_teams.length > 0) {
          detailedMessage += '\n\nYour current teams:';
          errorData.existing_teams.forEach((team: any) => {
            detailedMessage += `\n• ${team.team_name} (${team.league} - ${team.category})`;
          });
        }
        
        // Create error with additional metadata
        const error = new Error(detailedMessage);
        (error as any).errorType = 'ELIGIBILITY_VIOLATION';
        (error as any).errorData = errorData;
        throw error;
      }
      
      // Always prioritize backend message over HTTP status for all error types
      if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details) {
        errorMessage = errorData.details;
      }
    } catch (parseError) {
      // If we can't parse JSON, use the default error message
      if (!(parseError instanceof Error && parseError.message.includes('errorType'))) {
        console.warn('Could not parse error response:', parseError);
      } else {
        throw parseError; // Re-throw our custom eligibility error
      }
    }
    
    const error = new Error(errorMessage);
    (error as any).errorData = errorData;
    throw error;
  }
  
  return response.json();
};

// Token refresh function
const tryRefreshToken = async (): Promise<boolean> => {
  try {
    const token = getToken();
    if (!token) return false;

    const response = await fetch(API_URL.AUTH.REFRESH, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      if (data.access_token) {
        localStorage.setItem('token', data.access_token);
        return true;
      }
    }
    return false;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return false;
  }
};

// Wrapper around fetch that catches network errors (e.g. "Failed to fetch" from expired token / CORS)
// If user has a token but fetch fails at network level, auto-logout
const authFetch = async (url: string, options: RequestInit): Promise<Response> => {
  try {
    return await fetch(url, options);
  } catch (error) {
    if (error instanceof TypeError && getToken()) {
      // "Failed to fetch" while logged in — likely expired token causing CORS error
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/auth/signin';
    }
    throw error;
  }
};

// Generic HTTP methods
class ApiService {
  // GET request
  static async get<T = any>(url: string): Promise<T> {
    const response = await authFetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    return handleResponse<T>(response);
  }

  // POST request with JSON body
  static async post<T = any>(url: string, data?: any): Promise<T> {
    const response = await authFetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });

    return handleResponse<T>(response);
  }

  // PUT request with JSON body
  static async put<T = any>(url: string, data?: any): Promise<T> {
    const response = await authFetch(url, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });

    return handleResponse<T>(response);
  }

  // DELETE request
  static async delete<T = any>(url: string): Promise<T> {
    const response = await authFetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    return handleResponse<T>(response);
  }

  // POST request with FormData (for file uploads)
  static async postFormData<T = any>(url: string, formData: FormData): Promise<T> {
    const response = await authFetch(url, {
      method: 'POST',
      headers: getAuthHeadersForFormData(), // Don't set Content-Type for FormData
      body: formData,
    });

    return handleResponse<T>(response);
  }

  // POST request without authentication (for login, register, etc.)
  static async postPublic<T = any>(url: string, data?: any): Promise<T> {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: data ? JSON.stringify(data) : undefined,
    });

    return handleResponse<T>(response);
  }

  // GET request without authentication (for public endpoints)
  static async getPublic<T = any>(url: string): Promise<T> {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return handleResponse<T>(response);
  }
}

// Specific API endpoint methods
export const AuthAPI = {
  login: (credentials: { email: string; password: string }) =>
    ApiService.postPublic(API_URL.AUTH.LOGIN, credentials),

  register: (userData: { email: string; password: string; password_confirmation: string; full_name: string; phone: string }) =>
    ApiService.postPublic(API_URL.AUTH.REGISTER, userData),

  forgotPassword: (email: string) =>
    ApiService.postPublic(API_URL.AUTH.FORGOT_PASSWORD, { email }),

  resetPassword: (data: { token: string; email: string; password: string; password_confirmation: string }) =>
    ApiService.postPublic(API_URL.AUTH.RESET_PASSWORD, data),

  verifyEmail: (token: string) =>
    ApiService.postPublic(`${API_URL.AUTH.VERIFY_EMAIL}?token=${token}`),

  validateResetToken: (data: { token: string; email: string }) =>
    ApiService.postPublic(API_URL.AUTH.VALIDATE_RESET_TOKEN, data),

  changePassword: (data: { current_password: string; new_password: string; new_password_confirmation: string }) =>
    ApiService.post(API_URL.AUTH.CHANGE_PASSWORD, data),
};

export const UserAPI = {
  getProfile: () =>
    ApiService.get(API_URL.USER.PROFILE),
  
  updateProfile: (profileData: any) =>
    ApiService.put(API_URL.USER.PROFILE, profileData),
  
  uploadIdCard: (formData: FormData) =>
    ApiService.postFormData(API_URL.USER.UPLOAD_ID_CARD, formData),
  
  uploadStudentId: (formData: FormData) =>
    ApiService.postFormData(API_URL.USER.UPLOAD_STUDENT_ID, formData),
  
  uploadCV: (formData: FormData) =>
    ApiService.postFormData(API_URL.USER.UPLOAD_CV, formData),
};

export const DataAPI = {
  getCompanies: () =>
    ApiService.getPublic(API_URL.DATA.COMPANIES),
  
  getSchools: () =>
    ApiService.getPublic(API_URL.DATA.SCHOOLS),
};

export const TeamAPI = {
  createTeam: (teamData: { team_name: string; description?: string; league_id: number; category_id: number; company_id?: number }) =>
    ApiService.post(API_URL.TEAMS.CREATE, teamData),

  getTeamDetails: (teamId: string) =>
    ApiService.get(`${API_URL.TEAMS.DETAILS}/${teamId}/details`),

  joinTeam: (teamId: string) =>
    ApiService.post(API_URL.TEAMS.JOIN, { team_id: teamId }),

  getMyTeam: () =>
    ApiService.get(API_URL.TEAMS.MY_TEAM),

  getMyTeams: () =>
    ApiService.get(API_URL.TEAMS.MY_TEAMS),

  leaveTeam: () =>
    ApiService.post(API_URL.TEAMS.LEAVE),

  confirmMember: (teamId: string, memberId: number) =>
    ApiService.post(`${API_URL.TEAMS.CONFIRM_MEMBER}/${teamId}/confirm-member`, { member_id: memberId }),

  rejectMember: (teamId: string, memberId: number) =>
    ApiService.post(`${API_URL.TEAMS.REJECT_MEMBER}/${teamId}/reject-member`, { member_id: memberId }),

  cancelJoinRequest: (teamId: string) =>
    ApiService.post(`${API_URL.TEAMS.CANCEL_JOIN_REQUEST}/${teamId}/cancel-join-request`),

  signTerms: (teamId: string) =>
    ApiService.post(`${API_URL.TEAMS.SIGN_TERMS}/${teamId}/sign-terms`),

  submitForApproval: (teamId: string) =>
    ApiService.post(`${API_URL.TEAMS.SUBMIT_FOR_APPROVAL}/${teamId}/submit-for-approval`),
};

export const CompetitionAPI = {
  // Get all competitions for the current user
  getMyCompetitions: () =>
    ApiService.get(API_URL.COMPETITIONS.MY_COMPETITIONS),

  // Get team status for competition
  getTeamStatus: (teamId: number) =>
    ApiService.get(`${API_URL.COMPETITIONS.TEAM_STATUS}/${teamId}/status`),

  // Get competition brief for a team
  getBrief: (teamId: number) =>
    ApiService.get(`${API_URL.COMPETITIONS.GET_BRIEF}/${teamId}`),

  // Upload file for submission
  uploadFile: (formData: FormData) =>
    ApiService.postFormData(API_URL.COMPETITIONS.UPLOAD, formData),

  // Update big idea text
  updateBigIdea: (data: { team_id: number; big_idea: string }) =>
    ApiService.post(API_URL.COMPETITIONS.BIG_IDEA, data),

  // Submit entry for competition
  submitEntry: (teamId: number) =>
    ApiService.post(API_URL.COMPETITIONS.SUBMIT, { team_id: teamId }),

  // Get competition results
  getResults: (teamId: number) =>
    ApiService.get(`${API_URL.COMPETITIONS.RESULTS}/${teamId}`),

  // Get public competition schedule
  getSchedule: () =>
    ApiService.getPublic(API_URL.COMPETITIONS.SCHEDULE),
};

// Export the main service class and specific APIs
export { ApiService };
export default ApiService;